import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { Panel } from '../../components/ui/Panel';
import { Toggle } from '../../components/ui/Toggle';
import { useAccount } from '../../contexts/AccountContext';
import { annualPrice, plans } from '../../data/plans';
const domainPattern = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
export function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilled = (location.state as {
    domain?: string;
  } | null)?.domain ?? '';
  const {
    plan,
    planId,
    setPlanId,
    billingPeriod
  } = useAccount();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [domain, setDomain] = useState(prefilled);
  const [wantSsl, setWantSsl] = useState(true);
  const [wantUptime, setWantUptime] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const services = (wantSsl ? 1 : 0) + (wantUptime ? 1 : 0);
  const price = billingPeriod === 'annual' ? annualPrice(plan) / 12 : plan.monthlyPrice;
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      next.email = 'We need a valid address — this is where alerts go.';
    }
    if (password.length < 8) {
      next.password = 'Use at least 8 characters.';
    }
    if (!domainPattern.test(domain.trim())) {
      next.domain = 'Enter a domain like acme.com, without http:// or a path.';
    }
    if (services === 0) {
      next.checks = 'Choose at least one check for this domain.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      navigate('/checkout', {
        state: {
          domain: domain.trim(),
          wantSsl,
          wantUptime
        }
      });
    }, 700);
  }
  const aside = <Panel className="p-6" data-id="element-1781" data-mp="xfvmhed076zo">
      <div className="flex items-start justify-between gap-3" data-id="element-1782" data-mp="12cm2y2sq04lm">
        <div data-id="element-1783" data-mp="12c3v08wp41xv">
          <h2 className="text-sm font-semibold text-ink" data-id="element-1784" data-mp="12eq4wf726tj9">
            {plan.name} plan
          </h2>
          <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1785" data-mp="5ozash2oj8dz">
            {billingPeriod === 'annual' ? `$${annualPrice(plan)} a year` : `$${plan.monthlyPrice} a month`}
          </p>
        </div>
        <span className="tabular text-2xl font-semibold text-ink" data-id="element-1786" data-mp="jmxacgccd09f">
          ${price % 1 === 0 ? price : price.toFixed(2)}
          <span className="text-xs font-normal text-ink-soft" data-id="element-1787" data-mp="1q1erhe70ja67">/mo</span>
        </span>
      </div>

      <ul className="mt-5 space-y-2.5 border-t border-line pt-5" data-id="element-1788" data-mp="1v0hxp1yqbmfr">
        {plan.highlights.map(highlight => <li key={highlight} className="flex gap-2.5 text-[13px] text-ink" data-id="element-1789" data-mp="3fj262fxg3fm3">
            <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" data-id="element-1790" data-mp="3ak8tqqtw9uni" />
            {highlight}
          </li>)}
      </ul>

      <div className="mt-5 rounded-control border border-line bg-canvas px-4 py-3" data-id="element-1791" data-mp="30vh2kkwnredn">
        <p className="tabular text-[13px] font-medium text-ink" data-id="element-1792" data-mp="2wtn5snpl2uir">
          This domain uses {services} of {plan.services} services
        </p>
        <p className="mt-1 text-xs leading-relaxed text-ink-muted" data-id="element-1793" data-mp="2rsfz6rz6g1do">
          {plan.services - services > 0 ? `${plan.services - services} left for other sites, or for the second check on this one.` : 'That fills this plan — you can move up any time from Billing.'}
        </p>
      </div>

      <div className="mt-5 border-t border-line pt-4" data-id="element-1794" data-mp="l2ru6jwpuxcl">
        <p className="text-xs font-medium text-ink-muted" data-id="element-1795" data-mp="g48a6p1onvmv">Change plan</p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1796" data-mp="o5fkrelzg7ir">
          {plans.map(option => <button key={option.id} type="button" onClick={() => setPlanId(option.id)} aria-pressed={planId === option.id} className={`rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 ease-snap ${planId === option.id ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1797" data-mp="t48yn856m0jg">
              {option.name} · ${option.monthlyPrice}
            </button>)}
        </div>
      </div>
    </Panel>;
  return <AuthLayout title="Create your account" subtitle="Add your first domain now and it starts being watched the moment you subscribe." aside={aside} footer={<>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand hover:text-brand-hover" data-id="element-1799" data-mp="33y7xerrx4nd8">
            Log in
          </Link>
        </>} data-id="element-1798" data-mp="fv1svptxa9h">
      <form onSubmit={submit} className="space-y-4" noValidate data-id="element-1800" data-mp="2z0pvvmg8daf9">
        <Field label="Email address" htmlFor="signup-email" hint="Alerts go here first. You can add more recipients later." error={errors.email} data-id="element-1801" data-mp="3czpcq7xi4l0v">
          <TextInput id="signup-email" type="email" autoComplete="email" value={email} invalid={Boolean(errors.email)} onChange={event => setEmail(event.target.value)} data-id="element-1802" data-mp="3mwtzo9z68kiq" />
        </Field>

        <Field label="Password" htmlFor="signup-password" error={errors.password} data-id="element-1803" data-mp="3vri5c18ns482">
          <TextInput id="signup-password" type="password" autoComplete="new-password" value={password} invalid={Boolean(errors.password)} onChange={event => setPassword(event.target.value)} data-id="element-1804" data-mp="3rovhscj0t5go" />
        </Field>

        <Field label="First domain" htmlFor="signup-domain" hint="Just the hostname — we work out the rest." error={errors.domain} data-id="element-1805" data-mp="2yd6yj0oii84n">
          <TextInput id="signup-domain" placeholder="acme.com" value={domain} invalid={Boolean(errors.domain)} onChange={event => setDomain(event.target.value)} data-id="element-1806" data-mp="37b8uh1te2al2" />
        </Field>

        <fieldset className="rounded-control border border-line-strong px-4 py-3.5" data-id="element-1807" data-mp="3m0wu7ylx916o">
          <legend className="px-1 text-[13px] font-medium text-ink" data-id="element-1808" data-mp="3m2rhvkokv7eg">
            Checks for this domain
          </legend>
          <div className="mt-1 space-y-3" data-id="element-1809" data-mp="3v1cwj4qxi29y">
            <div className="flex items-center justify-between gap-4" data-id="element-1810" data-mp="3v135ddu5dp2o">
              <span className="text-[13px] text-ink" data-id="element-1811" data-mp="1vpahq7t91w2m">Certificate expiry</span>
              <Toggle checked={wantSsl} onChange={() => setWantSsl(v => !v)} label="Monitor certificate expiry" data-id="element-1812" data-mp="20nbyun747lbg" />
            </div>
            <div className="flex items-center justify-between gap-4" data-id="element-1813" data-mp="1odci5b6mh9p2">
              <span className="text-[13px] text-ink" data-id="element-1814" data-mp="1ob9l4771ygau">Uptime</span>
              <Toggle checked={wantUptime} onChange={() => setWantUptime(v => !v)} label="Monitor uptime" data-id="element-1815" data-mp="1jfb1sy0kzoha" />
            </div>
          </div>
          {errors.checks && <p className="mt-2.5 text-xs text-critical-ink" data-id="element-1816" data-mp="2c0ug5oghd40h">{errors.checks}</p>}
        </fieldset>

        <Button type="submit" size="lg" className="w-full" disabled={submitting} data-id="element-1817" data-mp="2721480gudl2f">
          {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1818" data-mp="1y35w2iu8m0wh" />}
          {submitting ? 'Creating account' : 'Continue to payment'}
        </Button>
        <p className="text-xs leading-relaxed text-ink-soft" data-id="element-1819" data-mp="qnhll6kiv303">
          No charge until you confirm on the next step. Cancel any time from
          Billing.
        </p>
      </form>
    </AuthLayout>;
}